import { forwardRef, useContext, useImperativeHandle } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { PDFDocument } from "pdf-lib";
import { BrandingContext } from "../../../BrandingProvider";
import { usePermissions } from "../../login/PermissionContext";
import { displayPatientName } from "../../../utils/displayPatientName";
import NavLogoFooter from "../../../assets/images/logo/NavLogo_new.png";
import {
  formatDateIST,
  formatAppointmentTypeForPdf,
  extractTimeFromDateString,
} from "../../doctor-console/Utils/UtilsFunctions";
import { isTodayOrFuture } from "../../utils/utils";

const PrintPhysiotherapy = forwardRef(
  (
    {
      sectionsData,
      patientData,
      doctorData,
      selectedContextDisplay,
      selectedContext,
      dateForAppointment,
      startTime,
      showFooter: showFooterProp = true,
      showTopHeader: showTopHeaderProp = true,
    },
    ref
  ) => {
    const brandingData = useContext(BrandingContext);
    const { userInfo } = usePermissions();

    // Get template config from brandingData (context API)
    const prescriptionConfigItem = brandingData?.find(
      (item) =>
        item.name === "prescription_config" || item.name === "template_config"
    );
    const templateConfigFromContext = prescriptionConfigItem?.content
      ? typeof prescriptionConfigItem.content === "string"
        ? JSON.parse(prescriptionConfigItem.content)
        : prescriptionConfigItem.content
      : null;

    // Get template from brandingData (context API) or fallback to userInfo
    const prescriptionTemplateItem = brandingData?.find(
      (item) =>
        item.name === "prescription_template" || item.name === "template"
    );
    const templateBase64 =
      prescriptionTemplateItem?.content || userInfo?.template || null;
    const hasTemplateBackground = Boolean(templateBase64);

    // Merge template config: context API > userInfo > props > defaults
    const templateConfig = {
      showTopHeader:
        templateConfigFromContext?.showTopHeader ??
        userInfo?.template_config?.showTopHeader ??
        showTopHeaderProp ??
        true,
      showFooter:
        templateConfigFromContext?.showFooter ??
        userInfo?.template_config?.showFooter ??
        showFooterProp ??
        true,
      extraBottomMargin:
        templateConfigFromContext?.extraBottomMargin ??
        userInfo?.template_config?.extraBottomMargin ??
        0,
      extraRightMargin:
        templateConfigFromContext?.extraRightMargin ??
        userInfo?.template_config?.extraRightMargin ??
        0,
      extraLeftMargin:
        templateConfigFromContext?.extraLeftMargin ??
        userInfo?.template_config?.extraLeftMargin ??
        0,
      extraTopMargin:
        templateConfigFromContext?.extraTopMargin ??
        userInfo?.template_config?.extraTopMargin ??
        0,
      useBackgroundTemplate:
        templateConfigFromContext?.useBackgroundTemplate ??
        userInfo?.template_config?.useBackgroundTemplate ??
        false,
      printOfsetTop:
        templateConfigFromContext?.printOfsetTop ??
        userInfo?.template_config?.printOfsetTop ??
        0,
      printOfsetLeft:
        templateConfigFromContext?.printOfsetLeft ??
        userInfo?.template_config?.printOfsetLeft ??
        0,
      showDoctorSignature:
        templateConfigFromContext?.showDoctorSignature ??
        userInfo?.doctor_requires_prescription_signature ??
        userInfo?.template_config?.showDoctorSignature ??
        true,
    };

    const collateralImage =
      brandingData?.find((item) => item.name === "collateral_image")?.content ||
      "";
    const overlayImageBase64 =
      brandingData?.find((item) => item.name === "prescription_overlay_image")
        ?.content ||
      sessionStorage.getItem("prescriptionOverlayImage") ||
      "";

    const pageWidth = 595.28;
    const pageHeight = 841.89;
    const baseMargin = 40;

    // Extract configuration values
    const {
      showTopHeader: configShowTopHeader = true,
      showFooter: configShowFooter = true,
      extraBottomMargin = 0,
      extraRightMargin = 0,
      extraLeftMargin = 0,
      extraTopMargin = 0,
      useBackgroundTemplate = false,
      printOfsetTop = 0,
      printOfsetLeft = 0,
      showDoctorSignature = true,
    } = templateConfig;

    // Calculate effective margins (base + extra from config)
    const EFFECTIVE_LEFT_MARGIN = baseMargin + extraLeftMargin;
    const EFFECTIVE_TOP_MARGIN = baseMargin + extraTopMargin;

    // Extra printable area offsets for pre-printed stationery
    const PRINT_OFFSET_LEFT = printOfsetLeft;
    const PRINT_OFFSET_TOP = printOfsetTop;

    // Optional overlay (watermark) on top of content
    const SHOW_RX_OVERLAY = false;
    const RX_OVERLAY_OPACITY = 0.08;

    const SHOW_FOOTER =
      typeof configShowFooter === "boolean"
        ? configShowFooter
        : typeof showFooterProp === "boolean"
          ? showFooterProp
          : true;

    const CONTENT_BOTTOM_MARGIN = 80;
    const EXTRA_CONTENT_BOTTOM_MARGIN = Number(extraBottomMargin ?? 0) || 0;
    const EFFECTIVE_BOTTOM_MARGIN =
      CONTENT_BOTTOM_MARGIN + EXTRA_CONTENT_BOTTOM_MARGIN;

    const EXTRA_RIGHT_MARGIN = Number(extraRightMargin ?? 0) || 0;
    const EFFECTIVE_RIGHT_MARGIN = baseMargin + EXTRA_RIGHT_MARGIN;

    const SHOW_TOP_HEADER =
      typeof configShowTopHeader === "boolean"
        ? configShowTopHeader
        : typeof showTopHeaderProp === "boolean"
          ? showTopHeaderProp
          : true;

    const hexToRgbArray = (hex) => {
      const bigint = parseInt(hex.replace("#", ""), 16);
      return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
    };

    const getScaledDimensions = (width, height, maxWidth, maxHeight) => {
      if (!width || !height) {
        return { width: maxWidth, height: maxHeight };
      }
      const widthRatio = maxWidth / width;
      const heightRatio = maxHeight / height;
      const scale = Math.min(widthRatio, heightRatio, 1);
      return { width: width * scale, height: height * scale };
    };

    const getImageType = (base64String) => {
      if (!base64String) return null;
      if (base64String.startsWith("/9j/")) return "jpeg";
      if (base64String.startsWith("iVBORw0KGgo")) return "png";
      return "jpeg";
    };

    const renderHeaderFooter = (data, doc) => {
      const midX = pageWidth / 2 + 10 + PRINT_OFFSET_LEFT;
      const currentY = EFFECTIVE_TOP_MARGIN + PRINT_OFFSET_TOP;

      // Optional overlay watermark
      try {
        const overlayCenterX = pageWidth / 2 + PRINT_OFFSET_LEFT / 2;
        const overlayCenterY = pageHeight / 2;
        if (SHOW_RX_OVERLAY) {
          if (doc.setGState && doc.GState) {
            doc.setGState(new doc.GState({ opacity: RX_OVERLAY_OPACITY }));
          }
          if (overlayImageBase64) {
            try {
              doc.addImage(
                `data:image/png;base64,${overlayImageBase64}`,
                "PNG",
                PRINT_OFFSET_LEFT,
                PRINT_OFFSET_TOP,
                pageWidth - 2 * (EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT),
                pageHeight - (PRINT_OFFSET_TOP + 120)
              );
            } catch (imgErr) {
              doc.setFontSize(140);
              doc.setTextColor(180, 180, 180);
              doc.text("Rx", overlayCenterX, overlayCenterY, {
                angle: -30,
                align: "center",
              });
            }
          } else {
            doc.setFontSize(140);
            doc.setTextColor(180, 180, 180);
            doc.text("Rx", overlayCenterX, overlayCenterY, {
              angle: -30,
              align: "center",
            });
          }
          if (doc.setGState && doc.GState) {
            doc.setGState(new doc.GState({ opacity: 1 }));
          }
        }
      } catch (e) {
        // no-op if watermark drawing fails
      }

      doc.setFontSize(14);
      doc.setTextColor("#000");

      const clinicAddress = [
        sessionStorage.getItem("facilityAddressLine1"),
        sessionStorage.getItem("facilityAddressLine2"),
        sessionStorage.getItem("facilityAddressLine3"),
      ]
        .filter(Boolean)
        .join(", ");

      doc.setFontSize(10);
      const doctorY = currentY + 40;
      doc.setFontSize(10);
      doc.setTextColor("#000");

      const rightColumnLineHeight = 12;
      const clinicAddressY = doctorY + 45;
      let wrappedClinicAddress = [];
      let leftY = doctorY;

      if (SHOW_TOP_HEADER) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text(
          "Prescription",
          EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          currentY
        );
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);

        if (collateralImage) {
          const collateralImageDataUrl = collateralImage.startsWith("data:")
            ? collateralImage
            : `data:image/jpeg;base64,${collateralImage}`;
          try {
            const imageProps = doc.getImageProperties(collateralImageDataUrl);
            const { width: scaledWidth, height: scaledHeight } =
              getScaledDimensions(imageProps.width, imageProps.height, 110, 50);
            doc.addImage(
              collateralImageDataUrl,
              imageProps.fileType || "JPEG",
              pageWidth - scaledWidth - 60,
              currentY - 20,
              scaledWidth,
              scaledHeight
            );
          } catch (imgErr) {
            // no-op
          }
        }

        // Doctor Name — bold, larger font
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.text(
          `Dr. ${doctorData?.name || ""}`,
          EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          doctorY
        );
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        leftY = doctorY + 16;

        // Qualifications
        const qualificationsText = `${doctorData?.qualifications || ""}`;
        if (qualificationsText.trim()) {
          const wrappedQualifications = doc.splitTextToSize(
            qualificationsText,
            pageWidth / 2 - 40
          );
          doc.text(
            wrappedQualifications,
            EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
            leftY
          );
          leftY += wrappedQualifications.length * 12;
        }

        // Registration No
        doc.text(
          `Reg No: ${doctorData?.registration_no ?? "NA"}`,
          EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          leftY
        );
        leftY += 12;

        // Contact
        if (doctorData?.phone_no) {
          doc.text(
            `Contact: ${doctorData.phone_no}`,
            EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
            leftY
          );
          leftY += 12;
        }

        // Email
        if (doctorData?.email) {
          doc.text(
            `Email: ${doctorData.email}`,
            EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
            leftY
          );
          leftY += 12;
        }

        const formattedDate = formatDateIST(dateForAppointment?.toISOString());
        doc.text(`Date: ${formattedDate.split(" ")[0]}`, midX, doctorY + 15);
        doc.text(
          isTodayOrFuture(dateForAppointment)
            ? `Time: ${formatDateIST(new Date().toISOString()).split(" ").slice(1).join(" ")}`
            : `Time: ${extractTimeFromDateString(startTime) || ""}`,
          midX,
          doctorY + 30
        );

        const rightColumnWidth = Math.max(80, pageWidth - midX - 40);
        const clinicAddressLine = `Clinic Address: ${clinicAddress}`;
        wrappedClinicAddress = doc.splitTextToSize(
          clinicAddressLine,
          rightColumnWidth
        );
        doc.text(wrappedClinicAddress, midX, clinicAddressY);
      }

      const patientY = Math.max(
        leftY + 5,
        clinicAddressY + wrappedClinicAddress.length * rightColumnLineHeight + 5
      );

      doc.setFontSize(10);
      const availableWidth =
        pageWidth -
        (EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT) -
        EFFECTIVE_RIGHT_MARGIN;
      const lineHeight = 12;
      const lineSpacing = 5;

      // LINE 1 — Patient Name (bold)
      const patientNameLine = [
        patientData?.patient_id ? `ID: ${patientData.patient_id}` : null,
        `Patient: ${displayPatientName(patientData?.name)}`,
      ]
        .filter(Boolean)
        .join(" | ");
      doc.setFont("helvetica", "bold");
      const wrappedPatientLine = doc.splitTextToSize(
        patientNameLine,
        availableWidth
      );
      doc.text(
        wrappedPatientLine,
        EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
        patientY
      );
      doc.setFont("helvetica", "normal");
      let patientSectionY =
        patientY + wrappedPatientLine.length * lineHeight + lineSpacing;

      const appointmentDateTime = (() => {
        if (!dateForAppointment) return null;
        const formattedDateTime = formatDateIST(dateForAppointment.toISOString());
        const datePart = formattedDateTime?.split(" ")?.[0];
        const timePart = isTodayOrFuture(dateForAppointment)
          ? formatDateIST(new Date().toISOString()).split(" ").slice(1).join(" ")
          : extractTimeFromDateString(startTime);
        if (!datePart && !timePart) return null;
        return `DT: ${[datePart, timePart].filter(Boolean).join(" ")}`;
      })();

      // LINE 2 — Appointment | Phone | Age | Gender | Date time
      const ageLine = [
        `Appt: ${formatAppointmentTypeForPdf(patientData?.appointment_type)}`,
        patientData?.phone_no ? `Ph: ${patientData.phone_no}` : null,
        patientData?.age ? `Age: ${patientData.age}y` : null,
        patientData?.gender ? `G: ${patientData.gender}` : null,
        appointmentDateTime,
      ]
        .filter(Boolean)
        .join(" | ");
      if (ageLine) {
        const wrappedAgeLine = doc.splitTextToSize(ageLine, availableWidth);
        doc.text(
          wrappedAgeLine,
          EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          patientSectionY
        );
        patientSectionY += wrappedAgeLine.length * lineHeight + lineSpacing;
      }

      // ABHA details on a single line when either field is present
      const abhaParts = [];
      if (patientData?.abha_id) {
        abhaParts.push(`ABHA Address: ${patientData.abha_id}`);
      }
      if (patientData?.abha_no) {
        abhaParts.push(`ABHA Number: ${patientData.abha_no}`);
      }
      if (abhaParts.length > 0) {
        const wrappedAbhaLine = doc.splitTextToSize(
          abhaParts.join(" | "),
          availableWidth
        );
        doc.text(
          wrappedAbhaLine,
          EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          patientSectionY
        );
        patientSectionY += wrappedAbhaLine.length * lineHeight + lineSpacing;
      }

      const separatorY = patientSectionY;
      doc.line(
        EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
        separatorY,
        pageWidth - EFFECTIVE_RIGHT_MARGIN,
        separatorY
      );
      // Return the Y position where body content should begin (just below the separator).
      return separatorY + 15;
    };

    const renderFooter = (doc, pageNumber) => {
      if (!SHOW_FOOTER) return;
      const themeColour = "#EEEEEE";
      const margin = 5;
      const baseFooterHeight = 56;
      const dynamicFooterHeight = baseFooterHeight;

      const pageBottomY = pageHeight - margin;
      const footerTopY = pageBottomY - dynamicFooterHeight;

      const footerWidth =
        pageWidth - EFFECTIVE_LEFT_MARGIN - EFFECTIVE_RIGHT_MARGIN;
      const footerLeftX = EFFECTIVE_LEFT_MARGIN;

      doc.setFillColor(...hexToRgbArray(themeColour));
      doc.rect(footerLeftX, footerTopY, footerWidth, dynamicFooterHeight, "F");

      const lineY = footerTopY + dynamicFooterHeight - 18;
      const textLeftX = footerLeftX + 10;
      const logoWidth = 40;
      const logoHeight = 18;
      const logoX = footerLeftX + footerWidth - logoWidth - 10;
      const disclaimerY = footerTopY + 12;
      const disclaimerWidth = footerWidth - 20;
      const disclaimerText =
        "NB: Patient is at liberty to buy any generic version of the medicine prescribed. In Emergency always consult your nearest ER or available doctor.";
      const poweredText =
        "Enhanced Patient Care Powered by Hlthtek Digital Healthcare Platform (Visit: https://www.hlthtek.com)";

      doc.setFont("helvetica", "bold");
      doc.setFontSize(6);
      doc.setTextColor("#000000");
      doc.text(disclaimerText, footerLeftX + 10, disclaimerY, {
        maxWidth: disclaimerWidth,
      });

      doc.setFontSize(9);
      doc.setTextColor("#000000");
      doc.text(poweredText, textLeftX, lineY);

      if (NavLogoFooter) {
        doc.addImage(
          NavLogoFooter,
          "PNG",
          logoX,
          lineY - logoHeight + 6,
          logoWidth,
          logoHeight
        );
      }
    };

    const ensureSpaceForSection = (doc, startY, minNeeded = 160) => {
      const available = pageHeight - startY - EFFECTIVE_BOTTOM_MARGIN;
      if (available < minNeeded) {
        doc.addPage();
        return renderHeaderFooter({}, doc);
      }
      return startY;
    };

    const renderDoctorSignatureBlock = (doc, startY) => {
      const signature = doctorData?.doctor_signature;
      const imgType = signature ? getImageType(signature) : null;

      const blockWidth = 180;
      const blockX = pageWidth - blockWidth - EFFECTIVE_RIGHT_MARGIN;
      let y = startY;

      doc.setFontSize(10);
      doc.setTextColor("#000");

      if (signature) {
        doc.addImage(
          `data:image/${imgType};base64,${signature}`,
          imgType.toUpperCase(),
          blockX,
          y,
          140,
          60
        );
        y += 70;
      }

      doc.setFont("helvetica", "bold");
      doc.text(`Dr. ${doctorData?.name || ""}`, blockX, y);
      y += 14;

      doc.setFont("helvetica", "normal");

      if (doctorData?.qualifications) {
        const wrappedQual = doc.splitTextToSize(
          doctorData.qualifications,
          blockWidth
        );
        doc.text(wrappedQual, blockX, y);
        y += wrappedQual.length * 12;
      }

      doc.text(`Reg No: ${doctorData?.registration_no ?? "NA"}`, blockX, y);
      return y;
    };

    const renderSection = (doc, y, title, headers, rows) => {
      if (!rows?.length) return y;
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(title, EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT, y);
      autoTable(doc, {
        startY: y + 8,
        head: [headers],
        body: rows,
        theme: "grid",
        styles: { fontSize: 8 },
        headStyles: {
          fillColor: hexToRgbArray("#EEEEEE"),
          textColor: hexToRgbArray("#000000"),
        },
        margin: {
          top: 40 + PRINT_OFFSET_TOP,
          left: EFFECTIVE_LEFT_MARGIN + PRINT_OFFSET_LEFT,
          right: EFFECTIVE_RIGHT_MARGIN,
          bottom: EFFECTIVE_BOTTOM_MARGIN,
        },
        didDrawPage: (data) => renderHeaderFooter(data, doc),
        pageBreak: "auto",
      });
      return doc.lastAutoTable.finalY + 16;
    };

    const buildDoc = async (withLetterhead) => {
      const doc = new jsPDF({
        orientation: "p",
        unit: "pt",
        format: "a4",
        compress: true,
      });

      const shouldUseTemplate =
        Boolean(withLetterhead) &&
        hasTemplateBackground &&
        Boolean(useBackgroundTemplate);

      // Start after header (dynamic — grows with qualifications / patient data).
      let currentY = renderHeaderFooter({}, doc);

      currentY = renderSection(
        doc,
        currentY,
        "Chief Complaint",
        ["Condition", "Clinical Status"],
        (sectionsData?.chiefComplaint || []).map((item) => [
          item.disease_description || "-",
          item.clinical_status_description || "-"
        ])
      );
      currentY = renderSection(
        doc,
        currentY,
        "Known Case Of",
        ["Diagnosis", "Clinical Status"],
        (sectionsData?.knownCaseOf || []).map((item) => [
          item.disease_description || "-",
          item.clinical_status_description || "-",
        ])
      );
      currentY = renderSection(
        doc,
        currentY,
        "History Of",
        ["Condition", "Onset Date", "Clinical Status"],
        (sectionsData?.historyOf || []).map((item) => [
          item.disease_description || "-",
          item.onset_datetime || "-",
          item.clinical_status_description || "-"
        ])
      );
      currentY = renderSection(
        doc,
        currentY,
        "On Observation",
        ["Observation", "Notes", "Observation Date"],
        (sectionsData?.onObservation || []).map((item) => [
          item.observation_description || "-",
          item.value_string || "-",
          item.observation_date || "-",
        ])
      );
      currentY = renderSection(
        doc,
        currentY,
        "On Examination - Upper Limb",
        ["Test", "Value/Qty", "Unit", "Notes", "Body Site", "Date"],
        (sectionsData?.upperLimbExam || []).map((item) => [
          item.observation_description || "-",
          item.value_quantity ?? "-",
          item.unit || "-",
          item.value_string || "-",
          item.body_site_description || "-",
          item.observation_date || "-",
        ])
      );
      currentY = renderSection(
        doc,
        currentY,
        "On Examination - Lower Limb",
        ["Test", "Notes", "Body Site", "Date"],
        (sectionsData?.lowerLimbExam || []).map((item) => [
          item.observation_description || "-",
          item.value_string || "-",
          item.body_site_description || "-",
          item.observation_date || "-",
        ])
      );
      currentY = renderSection(
        doc,
        currentY,
        "Advice",
        ["Advice"],
        (sectionsData?.advice || []).map((item) => [item.advice_text || "-"])
      );

      // Doctor signature block at the end
      if (showDoctorSignature) {
        currentY = ensureSpaceForSection(doc, currentY, 110);
        currentY = renderDoctorSignatureBlock(doc, currentY);
      }

      // Ensure header/footer is added on every page (even non-autoTable ones)
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i += 1) {
        doc.setPage(i);
        renderHeaderFooter({}, doc);
        if (SHOW_FOOTER) {
          renderFooter(doc, i);
        }
      }

      // Post-process: optionally compose a new PDF with the template as background
      let finalBytes;
      if (shouldUseTemplate) {
        try {
          const srcBytes = doc.output("arraybuffer");
          const templateBytes = Uint8Array.from(atob(templateBase64), (c) =>
            c.charCodeAt(0)
          );

          const newDoc = await PDFDocument.create();
          const srcDoc = await PDFDocument.load(srcBytes);
          const pageCount = srcDoc.getPageCount();

          for (let i = 0; i < pageCount; i += 1) {
            const [embeddedSrcPage] = await newDoc.embedPdf(srcBytes, [i]);
            const [embeddedTemplatePage] = await newDoc.embedPdf(
              templateBytes,
              [0]
            );

            const pageW = embeddedSrcPage.width;
            const pageH = embeddedSrcPage.height;

            const page = newDoc.addPage([pageW, pageH]);
            page.drawPage(embeddedTemplatePage, {
              x: 0,
              y: 0,
              width: pageW,
              height: pageH,
            });
            page.drawPage(embeddedSrcPage, {
              x: 0,
              y: 0,
              width: pageW,
              height: pageH,
            });
          }

          finalBytes = await newDoc.save();
        } catch (e) {
          console.error("PDF template overlay failed:", e);
          finalBytes = doc.output("arraybuffer");
        }
      } else {
        finalBytes = doc.output("arraybuffer");
      }

      const blob = new Blob([finalBytes], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      const printWindow = window.open(blobUrl, "_blank");

      if (printWindow) {
        printWindow.onload = () => {
          printWindow.focus();
          printWindow.print();
        };
      } else {
        alert("Please allow pop-ups for this site to print the document.");
      }
    };

    useImperativeHandle(ref, () => ({
      print: (withLetterhead) => buildDoc(withLetterhead),
    }));

    return null;
  }
);

export default PrintPhysiotherapy;
